import { createI18n } from 'vue-i18n'
import en from './locales/en.json'
import he from './locales/he.json'
import ar from './locales/ar.json'
import emitter from '@/utils/emitter'

// Define supported languages type
export type SupportedLanguage = 'en' | 'he' | 'ar';

// Get the language from localStorage or use browser default
const savedLanguage = localStorage.getItem('language') || navigator.language.split('-')[0] || 'en';
const validLanguage = ['en', 'he', 'ar'].includes(savedLanguage) ? savedLanguage : 'en';

// Safe manual clone to avoid deep clone issues
const messages = {
  en: { ...en },
  he: { ...he },
  ar: { ...ar }
};

// Create i18n instance with Legacy mode explicitly enabled for Vue 3
export const i18n = createI18n({
  legacy: false, // Must use legacy mode for Vue 3 template usage
  locale: validLanguage,
  fallbackLocale: 'en',
  messages,
  // Keep quiet about missing translations in production
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  missingWarn: false,
  fallbackWarn: false
});

// Function to change the language and handle RTL if needed
export function setLanguage(lang: SupportedLanguage) {
  // Save the language preference
  localStorage.setItem('language', lang);
  
  // Clear sidebar accordion state to force refresh
  localStorage.removeItem('trezo-open-sidebar-menus');
  
  // Set the i18n locale
  i18n.global.locale.value = lang;
  
  // Handle RTL languages (Hebrew and Arabic)
  const isRtl = ['he', 'ar'].includes(lang);
  const newDirection = isRtl ? 'rtl' : 'ltr';
  
  document.body.setAttribute('dir', newDirection);
  localStorage.setItem('direction', newDirection);
  
  // Force update directly on all elements with data-i18n-key
  setTimeout(() => {
    const elementsWithKeys = document.querySelectorAll<HTMLElement>('[data-i18n-key]');
    console.log(`Found ${elementsWithKeys.length} elements with translation keys to update`);
    
    if (elementsWithKeys.length > 0) {
      elementsWithKeys.forEach(el => {
        const key = el.getAttribute('data-i18n-key');
        if (key) {
          try {
            // Get translation from the correct dictionary
            const parts = key.split('.');
            let result: any = messages[lang];
            let translatedText: string | undefined;
            
            // Navigate through the nested objects
            let valid = true;
            for (const part of parts) {
              if (!result || typeof result !== 'object' || !(part in result)) {
                console.warn(`Missing translation key: ${key} in ${lang} locale`);
                valid = false;
                break;
              }
              result = result[part as keyof typeof result];
            }
            
            // Update element text if translation found
            if (valid && typeof result === 'string') {
              el.textContent = result;
              // Force repaint
              const forceRepaint = el.offsetHeight;
            } else {
              // Try fallback to English
              let enResult: any = messages['en'];
              let enValid = true;
              
              for (const part of parts) {
                if (!enResult || typeof enResult !== 'object' || !(part in enResult)) {
                  enValid = false;
                  break;
                }
                enResult = enResult[part as keyof typeof enResult];
              }
              
              if (enValid && typeof enResult === 'string') {
                el.textContent = enResult;
                const forceRepaint = el.offsetHeight;
              }
            }
          } catch (err) {
            console.warn(`Error updating translation for key ${key}:`, err);
          }
        }
      });
    }
  }, 50);
  
  // Emit language change event twice - once immediately and once after a delay
  // This helps ensure all components receive the notification
  emitter.emit('language:changed', lang);
  
  // Emit again after a short delay to ensure DOM has updated
  setTimeout(() => {
    emitter.emit('language:changed', lang);
  }, 100);
  
  // Force more aggressive DOM refresh for sidebar elements
  setTimeout(() => {
    const sidebarTitles = document.querySelectorAll('.sidebar-area .title');
    if (sidebarTitles.length > 0) {
      console.log('Forcing sidebar titles refresh');
      sidebarTitles.forEach(title => {
        // Force repaint by accessing offset
        const offset = (title as HTMLElement).offsetHeight;
      });
    }
  }, 300);
}

// Set initial direction
const isRtl = ['he', 'ar'].includes(validLanguage);
document.body.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
localStorage.setItem('direction', isRtl ? 'rtl' : 'ltr');

// Keep this utility function for future use if needed
export function reloadLanguage() {
  const currentLang = localStorage.getItem('language') || 'en';
  localStorage.setItem('reload_translations', 'true');
  location.reload();
}

export default i18n; 